import { timeParts } from '@commons/util/helper/time';

import { HypervisionService } from '../hypervision.service';
import { computeAvgProcessTime, computeContactRate, defaultColorFunc, rateFormatFunc, valueFunc } from './indicatrors.helper';

type Format = (value: number | string) => string;
type Value = (domain, metrics) => number;

export interface Indicator {
  name: string;
  format: Format;
  value: Value;
  color: Value;
  availableInSyntheticView: boolean;
  showColor: boolean;
  displayed?: boolean;
}

abstract class AbstractIndicator implements Indicator {
  abstract name: string;
  abstract format: Format;
  abstract value: Value;
  abstract color: Value;
  abstract availableInSyntheticView: boolean;
  abstract showColor: boolean;
  displayed?: boolean;
  constructor(protected readonly service: HypervisionService) {}
}

export class ProcessRate extends AbstractIndicator {
  name = 'processRate';
  availableInSyntheticView = true;
  showColor = true;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => rateFormatFunc(this.service.metric, value);
  value = (_, metrics) => computeContactRate(metrics, 'SUM_CTT_SUCCESS_DOMAIN');
  color = (domain, metrics) => this.value(domain, metrics);
}

export class GaveUpRate extends AbstractIndicator {
  name = 'gaveUpRate';
  availableInSyntheticView = true;
  showColor = true;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => rateFormatFunc(this.service.metric, value);
  value = (_, metrics) => computeContactRate(metrics, 'SUM_CTT_GAVE_UP_DOMAIN');
  color = (domain, metrics) => 100 - this.value(domain, metrics);
}

export class DissuadedRate extends AbstractIndicator {
  name = 'dissuadedRate';
  availableInSyntheticView = true;
  showColor = true;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => rateFormatFunc(this.service.metric, value);
  value = (_, metrics) => computeContactRate(metrics, 'SUM_CTT_DISSUADED_DOMAIN');
  color = (domain, metrics) => 100 - this.value(domain, metrics);
}

export class Waiting extends AbstractIndicator {
  name = 'waiting';
  color = defaultColorFunc;
  availableInSyntheticView = true;
  showColor = false;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => valueFunc(this.service.metric, value);
  value = (_, metrics) => metrics.SUM_CTT_WAITING_DOMAIN_NOW;
}

export class AvgProcessTime extends AbstractIndicator {
  name = 'avgProcessTime';
  color = defaultColorFunc;
  availableInSyntheticView = true;
  showColor = false;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => {
    if (value === 0 && !this.service.metric.isChannelSubscribed()) {
      return this.service.$i18next.t('hypervision:indicator.avgProcessTimeFormat', {
        hour: '-',
        min: '-',
        second: '-',
      });
    }
    let tp = timeParts(value);

    return this.service.$i18next.t('hypervision:indicator.avgProcessTimeFormat', {
      hour: tp[0],
      min: tp[1],
      second: tp[2],
    });
  };
  value = (_, metrics) => computeAvgProcessTime(metrics);
}

export class State extends AbstractIndicator {
  name = 'state';
  availableInSyntheticView = true;
  showColor = true;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => {
    if (value === 'root') {
      return '-';
    }
    if (value) {
      return this.service.$i18next.t('hypervision:indicator.opened');
    }
    return this.service.$i18next.t('hypervision:indicator.closed');
  };
  value = (domain, _) => {
    if (this.service.isRoot(domain)) {
      return 'root';
    }
    return domain.open;
  };
  color = (domain, metrics) => {
    if (this.service.isRoot(domain)) {
      let domainKey;
      let nbDomains = 0;
      let nbOpenedDomains = 0;
      for (domainKey in this.service.domainsMetrics || []) {
        nbDomains++;
        if (this.service.domainsMetrics![domainKey].open) {
          nbOpenedDomains++;
        }
      }
      return (nbOpenedDomains / Math.max(1, nbDomains)) * 100;
    }
    if (this.value(domain, metrics)) {
      return 100;
    }
    return 0;
  };
}

export class Available extends AbstractIndicator {
  name = 'available';
  color = defaultColorFunc;
  availableInSyntheticView = false;
  showColor = false;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => valueFunc(this.service.metric, value);
  value = (_, metrics) => metrics.SUM_CSR_AVAILABLE_DOMAIN_NOW;
}

export class Busy extends AbstractIndicator {
  name = 'busy';
  color = defaultColorFunc;
  availableInSyntheticView = false;
  showColor = false;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => valueFunc(this.service.metric, value);
  value = (_, metrics) => metrics.SUM_CSR_WORKING_DOMAIN_NOW;
}

export class Pause extends AbstractIndicator {
  name = 'pause';
  color = defaultColorFunc;
  availableInSyntheticView = false;
  showColor = false;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => valueFunc(this.service.metric, value);
  value = (_, metrics) => metrics.SUM_CSR_NOT_WORKING_DOMAIN_NOW;
}

export class Task extends AbstractIndicator {
  name = 'task';
  color = defaultColorFunc;
  availableInSyntheticView = false;
  showColor = false;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => valueFunc(this.service.metric, value);
  value = (_, metrics) => metrics.SUM_CSR_TASK_DOMAIN_NOW;
}

export class ContactOffered extends AbstractIndicator {
  name = 'contactOffered';
  color = defaultColorFunc;
  availableInSyntheticView = false;
  showColor = false;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => valueFunc(this.service.metric, value);
  value = (_, metrics) => metrics.SUM_CTT_CREATED_DOMAIN;
}

export class ContactHandled extends AbstractIndicator {
  name = 'contactHandled';
  color = defaultColorFunc;
  availableInSyntheticView = false;
  showColor = false;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => valueFunc(this.service.metric, value);
  value = (_, metrics) => metrics.SUM_CTT_SUCCESS_DOMAIN;
}

export class ContactAbandoned extends AbstractIndicator {
  name = 'contactAbandoned';
  color = defaultColorFunc;
  availableInSyntheticView = false;
  showColor = false;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => valueFunc(this.service.metric, value);
  value = (_, metrics) => metrics.SUM_CTT_GAVE_UP_DOMAIN;
}

export class ContactDissuaded extends AbstractIndicator {
  name = 'contactDissuaded';
  color = defaultColorFunc;
  availableInSyntheticView = false;
  showColor = false;
  constructor(service: HypervisionService) {
    super(service);
  }
  format = value => valueFunc(this.service.metric, value);
  value = (_, metrics) => metrics.SUM_CTT_DISSUADED_DOMAIN;
}
